import React, { useEffect, useRef, useState } from 'react';
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import './metodoClinicaInteligente.css';
import heroImage from '../../assets/images/hero_lp_pratica_inteligente.png';
const MetodoClinicaInteligente = () => {
    const sectionRefs = useRef([]);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('appear');
                    }
                });
            },
            { threshold: 0.1 }
        );

        sectionRefs.current.forEach((ref) => {
            if (ref) observer.observe(ref);
        });

        return () => {
            window.removeEventListener('resize', handleResize);
            sectionRefs.current.forEach((ref) => {
                if (ref) observer.unobserve(ref);
            });
        };
    }, []);

    const sliderSettings = {
        dots: true,
        infinite: true,
        speed: 800,
        slidesToShow: 3,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 3500,
        pauseOnHover: true,
        arrows: true,
        responsive: [
            {
                breakpoint: 1024,
                settings: "unslick" // This is the slick way to disable on smaller screens
            }
        ]
    };

    const addToRefs = (el) => {
        if (el && !sectionRefs.current.includes(el)) {
            sectionRefs.current.push(el);
        }
    };

    // SVG Icons
    const IconClock = () => (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
    );
    const IconSparkles = () => (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.912 5.885L20 10.8l-6.088 1.915L12 18.6l-1.912-5.885L4 10.8l6.088-1.915L12 3z"></path></svg>
    );
    const IconCheck = () => (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
    );
    const IconX = () => (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
    );

    return (
        <div className="lp-container">
            {/* HERO SECTION */}
            <section className="lp-section lp-hero" ref={addToRefs}>
                <div className="lp-hero-content stagger-item stagger-1">
                    <span className="lp-hero-badge">Exclusivo para Psicólogos</span>
                    <h1 className="lp-hero-headline">
                        Sua prática clínica, <br />
                        <strong>elevada pela IA.</strong>
                    </h1>
                    <p className="lp-hero-subheadline">
                        Transforme horas de burocracia em minutos de foco no que realmente importa: seus pacientes. Use a IA de forma ética, prática e segura para otimizar sua rotina.
                    </p>
                    <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
                        <a href="#checkout" className="lp-cta-btn">
                            Quero Começar Agora
                        </a>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', opacity: 0.7 }}>
                            <IconCheck /> <span style={{ fontSize: '1.4rem' }}>Acesso imediato</span>
                        </div>
                    </div>
                </div>

                <div className="lp-hero-visual stagger-item stagger-2">
                    <div className="lp-hero-image-container">
                        <img 
                            src={heroImage} 
                            alt="Psicóloga usando IA" 
                            className="lp-hero-image"
                        />
                    </div>
                    <div className="lp-floating-card card-1">
                        <div className="lp-icon-circle" style={{ margin: 0, width: '40px', height: '40px', fontSize: '2rem' }}>⏱️</div>
                        <div>
                            <p style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700 }}>+ Eficiência</p>
                            <p style={{ margin: 0, fontSize: '1rem', opacity: 0.6 }}>+ Tempo Livre</p>
                        </div>
                    </div>
                    <div className="lp-floating-card card-2">
                        <div className="lp-icon-circle" style={{ margin: 0, width: '40px', height: '40px', fontSize: '2rem', background: 'rgba(94, 107, 94, 0.1)', color: 'var(--lp-accent)' }}>🤖</div>
                        <div>
                            <p style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700 }}>Agentes de IA</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* PAIN POINTS SECTION */}
            <section className="lp-section" ref={addToRefs}>
                <div style={{ textAlign: 'center' }} className="stagger-item stagger-1">
                    <p className="section-subtitle">O Cenário Atual</p>
                    <h2 className="section-title">Psi, você se sente assim?</h2>
                </div>
                
                {windowWidth > 1024 ? (
                    <Slider {...sliderSettings} className="lp-carousel">
                        {[
                            { icon: '😫', text: 'Termina o dia exausta, com a sensação de que ainda falta tudo?' },
                            { icon: '🔥', text: 'Vive apagando incêndio, organizando coisas de última hora?' },
                            { icon: '📉', text: 'Sente que não consegue evoluir porque falta tempo para o que importa?' },
                            { icon: '⌛', text: 'Sente que os relatórios e documentos estão sempre acumulados?' }
                        ].map((item, index) => (
                            <div key={index} className="lp-carousel-item">
                                <div className="lp-glass-card">
                                    <div className="lp-icon-circle">{item.icon}</div>
                                    <p style={{ fontSize: '1.8rem', fontWeight: 500, color: 'var(--lp-text)' }}>{item.text}</p>
                                </div>
                            </div>
                        ))}
                    </Slider>
                ) : (
                    <div className="lp-grid">
                        {[
                            { icon: '😫', text: 'Termina o dia exausta, com a sensação de que ainda falta tudo?' },
                            { icon: '🔥', text: 'Vive apagando incêndio, organizando coisas de última hora?' },
                            { icon: '📉', text: 'Sente que não consegue evoluir porque falta tempo para o que importa?' },
                            { icon: '⌛', text: 'Sente que os relatórios e documentos estão sempre acumulados?' }
                        ].map((item, index) => (
                            <div key={index} className={`lp-glass-card stagger-item stagger-${index + 1}`}>
                                <div className="lp-icon-circle">{item.icon}</div>
                                <p style={{ fontSize: '1.8rem', fontWeight: 500, color: 'var(--lp-text)' }}>{item.text}</p>
                            </div>
                        ))}
                    </div>
                )}

                <div className="stagger-item stagger-4" style={{ marginTop: '8rem', textAlign: 'center', padding: '6rem', borderRadius: '40px', background: 'var(--lp-bg-accent)', border: '1px solid rgba(140, 115, 85, 0.1)' }}>
                    <p style={{ fontSize: '2rem', color: 'var(--lp-text-light)', marginBottom: '1rem' }}>👉 O problema não é falta de dedicação.</p>
                    <p style={{ fontSize: '3.2rem', color: 'var(--lp-primary)', fontWeight: 700, maxWidth: '800px', margin: '0 auto' }}>
                        É que você está fazendo tudo no <strong>modo manual.</strong>
                    </p>
                </div>
            </section>

            {/* AHA MOMENT / VIRADA DE CHAVE */}
            <section className="lp-section" ref={addToRefs} style={{ background: 'var(--lp-bg-accent)', maxWidth: '100%', width: '100%', borderRadius: 0 }}>
                <div className="stagger-item stagger-1" style={{ maxWidth: 'var(--lp-container-width)', margin: '0 auto', textAlign: 'center' }}>
                    <p className="section-subtitle">💡 Virada de Chave</p>
                    <h2 className="section-title">A IA não veio para te substituir.</h2>
                    <p style={{ fontSize: '2rem', maxWidth: '800px', margin: '0 auto 6rem' }}>
                        Ela veio para tirar de você tudo aquilo que te consome tempo, te sobrecarrega e não precisa ser feito do zero.
                    </p>

                    <div className="lp-grid">
                        <div className="lp-glass-card">
                            <div className="lp-icon-circle">🧠</div>
                            <h3>Mente Clínica</h3>
                            <p>Você continua sendo a estrategista, a responsável pelas decisões e pela escuta empática.</p>
                        </div>
                        <div className="lp-glass-card" style={{ background: 'var(--lp-primary)', color: 'white' }}>
                            <div className="lp-icon-circle" style={{ background: 'rgba(255,255,255,0.2)', color: 'white' }}>🤖</div>
                            <h3 style={{ color: 'white' }}>Assistente IA</h3>
                            <p style={{ color: 'rgba(255,255,255,0.8)' }}>Assume todo o operacional cansativo, estruturação de dados e formatação.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* BENEFITS SECTION */}
            <section className="lp-section" ref={addToRefs}>
                <div className="stagger-item stagger-1" style={{ textAlign: 'center' }}>
                    <p className="section-subtitle">🚀 Resultados</p>
                    <h2 className="section-title">O que você vai conseguir</h2>
                </div>
                
                {windowWidth > 1024 ? (
                    <Slider {...sliderSettings} className="lp-carousel">
                        {[
                            { icon: <IconClock />, title: 'Relatórios Ágeis', desc: 'Fazer relatórios em uma fração do tempo.' },
                            { icon: '✨', title: 'Caos Mental Zero', desc: 'Organizar sua rotina sem sobrecarga operacional.' },
                            { icon: '🧠', title: 'Sessões Claras', desc: 'Planejar sessões com mais clareza científica.' },
                            { icon: '✏️', title: 'Conteúdo Fluido', desc: 'Criar conteúdos para redes sociais com facilidade.' },
                            { icon: '🌿', title: 'Mais Vida', desc: 'Tempo para estudar, descansar e viver além do consultório.' }
                        ].map((item, index) => (
                            <div key={index} className="lp-carousel-item">
                                <div className="lp-glass-card">
                                    <div className="lp-icon-circle">{item.icon}</div>
                                    <h3>{item.title}</h3>
                                    <p>{item.desc}</p>
                                </div>
                            </div>
                        ))}
                    </Slider>
                ) : (
                    <div className="lp-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
                        {[
                            { icon: <IconClock />, title: 'Relatórios Ágeis', desc: 'Fazer relatórios em uma fração do tempo.' },
                            { icon: '✨', title: 'Caos Mental Zero', desc: 'Organizar sua rotina sem sobrecarga operacional.' },
                            { icon: '🧠', title: 'Sessões Claras', desc: 'Planejar sessões com mais clareza científica.' },
                            { icon: '✏️', title: 'Conteúdo Fluido', desc: 'Criar conteúdos para redes sociais com facilidade.' },
                            { icon: '🌿', title: 'Mais Vida', desc: 'Tempo para estudar, descansar e viver além do consultório.' }
                        ].map((item, index) => (
                            <div key={index} className={`lp-glass-card stagger-item stagger-${index + 1}`}>
                                <div className="lp-icon-circle">{item.icon}</div>
                                <h3>{item.title}</h3>
                                <p>{item.desc}</p>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* IMPACT QUOTE */}
            <div className="lp-impact-quote stagger-item stagger-1" ref={addToRefs}>
                “Não é sobre trabalhar menos. <br /> É sobre parar de desperdiçar tempo.”
            </div>

            {/* OBJECTION HANDLING */}
            <section className="lp-section" ref={addToRefs}>
                <div style={{ textAlign: 'center' }} className="stagger-item stagger-1">
                    <p className="section-subtitle">⚠️ Importante</p>
                    <h2 className="section-title">Quebrando Mitos sobre a IA</h2>
                </div>

                <div className="lp-comparison-grid">
                    <div className="lp-comp-card no stagger-item stagger-1">
                        <h3 style={{ color: '#c53030', display: 'flex', alignItems: 'center', gap: '1rem' }}><IconX size={20} /> Isso NÃO é:</h3>
                        <ul className="lp-comp-list">
                            <li className="lp-comp-item">Substituição do psicólogo</li>
                            <li className="lp-comp-item">Algo antiético ou impessoal</li>
                            <li className="lp-comp-item">"Receita pronta" sem pensamento</li>
                        </ul>
                    </div>
                    <div className="lp-comp-card yes stagger-item stagger-2">
                        <h3 style={{ color: '#2f855a', display: 'flex', alignItems: 'center', gap: '1rem' }}><IconCheck size={20} /> Isso É:</h3>
                        <ul className="lp-comp-list">
                            <li className="lp-comp-item">Ferramenta de suporte</li>
                            <li className="lp-comp-item">Otimização máxima de tempo</li>
                            <li className="lp-comp-item">Inteligência aplicada à clínica</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* WHAT YOU WILL LEARN */}
            <section className="lp-section" ref={addToRefs} style={{ background: 'var(--lp-bg-accent)', maxWidth: '100%', width: '100%', borderRadius: 0 }}>
                <div className="stagger-item stagger-1" style={{ maxWidth: 'var(--lp-container-width)', margin: '0 auto', textAlign: 'center' }}>
                    <p className="section-subtitle">📦 Conteúdo</p>
                    <h2 className="section-title">O que você vai dominar</h2>
                    
                    <div className="lp-checklist">
                        {[
                            'Como usar IA mesmo sem entender nada de tecnologia',
                            'Como transformar anotações em relatórios rapidamente',
                            'Como organizar sua rotina clínica de ponta a ponta',
                            'Como usar IA como apoio terapêutico e insights',
                            'Como criar seus próprios GPTs personalizados',
                            'Como produzir conteúdo sem travar na folha em branco',
                            'Ferramentas: ChatGPT, Grok, Heygen e Kling AI'
                        ].map((item, index) => (
                            <div key={index} className="lp-check-item stagger-item">
                                <div style={{ color: 'var(--lp-primary)' }}><IconCheck /></div>
                                <span style={{ fontSize: '1.6rem', fontWeight: 500 }}>{item}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* DIFFERENTIATORS SECTION */}
            <section className="lp-section" ref={addToRefs}>
                <div style={{ textAlign: 'center' }} className="stagger-item stagger-1">
                    <p className="section-subtitle">⏱️ Diferenciais</p>
                    <h2 className="section-title">Por que este método é único?</h2>
                </div>
                
                <div className="lp-feature-grid">
                    {[
                        { icon: '🚀', title: 'Aulas Diretas', desc: 'Sem enrolação. Conteúdo prático para aplicação imediata.' },
                        { icon: '🗣️', title: 'Linguagem Simples', desc: 'Esqueça o "tecnicismo". Falamos a língua do psicólogo.' },
                        { icon: '🛠️', title: 'Uso Real', desc: 'Ferramentas validadas no dia a dia da clínica.' },
                        { icon: '💡', title: 'Inovação Ética', desc: 'Foco total na segurança e ética profissional.' }
                    ].map((item, index) => (
                        <div key={index} className="lp-feature-item stagger-item">
                            <div className="lp-icon-circle" style={{ margin: '0 auto' }}>{item.icon}</div>
                            <h3>{item.title}</h3>
                            <p style={{ margin: 0 }}>{item.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* AUDIENCE SECTION (PARA QUEM É / NÃO É) */}
            <section className="lp-section" ref={addToRefs}>
                <div style={{ textAlign: 'center' }} className="stagger-item stagger-1">
                    <p className="section-subtitle">🧠 Público</p>
                    <h2 className="section-title">Este curso é para você?</h2>
                </div>

                <div className="lp-audience-split">
                    <div className="lp-audience-card positive stagger-item stagger-1">
                        <h3 style={{ color: '#2f855a' }}>✅ Ideal para você se...</h3>
                        <div className="lp-audience-list">
                            <div className="lp-audience-item">
                                <span className="icon"><IconCheck /></span>
                                <span>Psicólogo clínico que se sente <strong>sobrecarregado</strong> com burocracia.</span>
                            </div>
                            <div className="lp-audience-item">
                                <span className="icon"><IconCheck /></span>
                                <span>Quem perde horas preciosas em tarefas operacionais e repetitivas.</span>
                            </div>
                            <div className="lp-audience-item">
                                <span className="icon"><IconCheck /></span>
                                <span>Quem busca <strong>mais tempo livre</strong> sem perder a qualidade clínica.</span>
                            </div>
                        </div>
                    </div>

                    <div className="lp-audience-card negative stagger-item stagger-2">
                        <h3 style={{ color: '#c53030' }}>❌ NÃO é para você se...</h3>
                        <div className="lp-audience-list">
                            <div className="lp-audience-item">
                                <span className="icon"><IconX /></span>
                                <span>Quem quer que a IA <strong>pense</strong> ou substitua sua decisão clínica.</span>
                            </div>
                            <div className="lp-audience-item">
                                <span className="icon"><IconX /></span>
                                <span>Quem busca "atalhos mágicos" e irresponsáveis.</span>
                            </div>
                            <div className="lp-audience-item">
                                <span className="icon"><IconX /></span>
                                <span>Quem não está disposto a aprender novos processos éticos.</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* BONUS SECTION */}
            <section className="lp-section" ref={addToRefs}>
                <div style={{ textAlign: 'center' }} className="stagger-item stagger-1">
                    <p className="section-subtitle">🎁 Presentes</p>
                    <h2 className="section-title">Bônus Estratégicos</h2>
                </div>
                <div className="lp-bonus-grid">
                    {[
                        { title: 'Modelos de Prompts', tag: 'PDF' },
                        { title: 'Estruturas de Relatórios', tag: 'Prático' },
                        { title: 'GPTs Configurados', tag: 'Exclusivo' },
                        { title: 'Roteiros de Conteúdo', tag: 'Validado' }
                    ].map((item, index) => (
                        <div key={index} className="lp-bonus-card stagger-item">
                            <span className="lp-bonus-tag">{item.tag}</span>
                            <h4 style={{ margin: 0, fontSize: '1.8rem' }}>{item.title}</h4>
                        </div>
                    ))}
                </div>
            </section>

            {/* FINAL CTA & GUARANTEE */}
            <section className="lp-section" ref={addToRefs} style={{ textAlign: 'center', paddingBottom: '15rem' }}>
                <div className="lp-glass-card stagger-item stagger-1" style={{ maxWidth: '800px', margin: '0 auto 8rem', background: 'var(--lp-text)', color: 'white' }}>
                    <div style={{ fontSize: '3.2rem', marginBottom: '1.5rem' }}>🔐 Garantia Risco Zero</div>
                    <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '1.8rem', maxWidth: '600px', margin: '0 auto' }}>
                        Você pode testar. Se não fizer sentido para você dentro de 7 dias, é só sair. Devolvemos seu investimento sem burocracia.
                    </p>
                </div>

                <h2 className="lp-hero-headline stagger-item stagger-1" style={{ fontSize: '5rem' }}>
                    Sua clínica mais inteligente começa hoje.
                </h2>
                <div className="stagger-item stagger-3" style={{ marginTop: '4rem' }}>
                    <a href="#checkout" className="lp-cta-btn" style={{ fontSize: '2.4rem', padding: '2.5rem 6rem' }}>
                        Matricular-se Agora
                    </a>
                </div>
            </section>
        </div>
    );
};

export default MetodoClinicaInteligente;
